import {
    Box,
    Flex,
    Popover,
    PopoverBody,
    PopoverContent,
    PopoverTrigger,
    Portal,
    Spinner,
    Text,
} from '@chakra-ui/react';
import { useState } from 'react';
import {
    CheckboxMenuList,
    CheckboxMenuSearch,
    CheckboxMenuTrigger,
    MenuRow,
    checkboxMenuColors,
    checkboxMenuPopoverContentSx,
} from '../../ui';
import type { TypeaheadOption } from './InventoryTypeaheadField';

export interface InventorySupplierDropdownProps {
    /** Selected supplier id ('' when none). */
    value: string;
    /** Selected supplier name shown as the trigger token. */
    displayLabel?: string;
    options: TypeaheadOption[];
    onSelect: (option: TypeaheadOption) => void;
    onSearch?: (query: string) => void;
    isLoading?: boolean;
    isDisabled?: boolean;
    placeholder?: string;
}

/**
 * Single-select supplier picker in the CheckboxMenu family (same look as the
 * vendor dropdown on the order screens). Deliberately search-first: the org's
 * supplier list is long, so rows only render once the user starts typing.
 */
const InventorySupplierDropdown = ({
    value,
    displayLabel,
    options,
    onSelect,
    onSearch,
    isLoading = false,
    isDisabled = false,
    placeholder = 'Search suppliers…',
}: InventorySupplierDropdownProps) => {
    const [isOpen, setIsOpen] = useState(false);
    const [query, setQuery] = useState('');

    const close = () => {
        setIsOpen(false);
        setQuery('');
        onSearch?.('');
    };

    const hasQuery = query.trim().length > 0;

    return (
        <Popover
            isOpen={isOpen}
            onClose={close}
            matchWidth
            placement="bottom-start"
            gutter={8}
        >
            <PopoverTrigger>
                <Box opacity={isDisabled ? 0.5 : undefined}>
                    <CheckboxMenuTrigger
                        isOpen={isOpen}
                        tokens={value && displayLabel ? [displayLabel] : []}
                        emptyLabel={placeholder}
                        emptyLabelColor="fg.muted"
                        onClick={isDisabled ? undefined : () => setIsOpen((prev) => !prev)}
                        minH="2rem"
                        py="1px"
                        cursor={isDisabled ? 'not-allowed' : undefined}
                    />
                </Box>
            </PopoverTrigger>
            <Portal>
                <PopoverContent
                    w="var(--popper-reference-width)"
                    sx={checkboxMenuPopoverContentSx}
                    data-inventory-floating-menu=""
                >
                    <PopoverBody p={0}>
                        <CheckboxMenuSearch
                            value={query}
                            onChange={(next) => {
                                setQuery(next);
                                onSearch?.(next);
                            }}
                            placeholder="Search suppliers…"
                            autoFocus
                        />
                        <CheckboxMenuList>
                            {!hasQuery && options.length > 0 ? (
                                // Preview only — the full list needs a query.
                                options.slice(0, 3).map((option) => (
                                    <MenuRow
                                        key={option.value}
                                        kind="single"
                                        isSelected={option.value === value}
                                        onClick={() => {
                                            onSelect(option);
                                            close();
                                        }}
                                    >
                                        {option.label}
                                    </MenuRow>
                                ))
                            ) : !hasQuery ? (
                                <Text
                                    fontSize="13px"
                                    color={checkboxMenuColors.muted}
                                    px="10px"
                                    py="8px"
                                >
                                    Type to search suppliers
                                </Text>
                            ) : isLoading && options.length === 0 ? (
                                <Flex align="center" justify="center" py="10px">
                                    <Spinner size="xs" />
                                </Flex>
                            ) : options.length === 0 ? (
                                <Text
                                    fontSize="13px"
                                    color={checkboxMenuColors.muted}
                                    px="10px"
                                    py="8px"
                                >
                                    No matching suppliers
                                </Text>
                            ) : (
                                options.map((option) => (
                                    <MenuRow
                                        key={option.value}
                                        kind="single"
                                        isSelected={option.value === value}
                                        onClick={() => {
                                            onSelect(option);
                                            close();
                                        }}
                                    >
                                        {option.label}
                                    </MenuRow>
                                ))
                            )}
                        </CheckboxMenuList>
                    </PopoverBody>
                </PopoverContent>
            </Portal>
        </Popover>
    );
};

export default InventorySupplierDropdown;
